import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkle, CoatHanger, ChatCircleDots, Heart } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const HERO_IMG = "https://images.unsplash.com/photo-1751399566443-a07d07344bdf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxzdHlsaXNoJTIwY29uZmlkZW50JTIwd29tYW4lMjBmYXNoaW9ufGVufDB8fHx8MTc3NjYzMzE5N3ww&ixlib=rb-4.1.0&q=85";
const GALLERY_IMG = "https://images.pexels.com/photos/5424945/pexels-photo-5424945.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const OCCASION_IMG = "https://images.pexels.com/photos/1148960/pexels-photo-1148960.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  return (
    <div className="min-h-screen bg-cream-50 text-charcoal-800">
      {/* Nav */}
      <header className="sticky top-0 z-40 glass-header border-b border-cream-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkle weight="fill" size={22} className="text-charcoal-700" />
            <span className="font-serif text-2xl tracking-tight">Ava</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login" className="text-sm text-charcoal-700 hover:underline">Log in</Link>
            <Link to="/register" data-testid="nav-signup" className="btn-primary-pill text-sm py-2 px-5">Get started</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <p className="text-[0.7rem] tracking-[0.32em] uppercase text-charcoal-500 mb-6" data-testid="hero-kicker">
              Your personal stylist · Always on
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-charcoal-900">
              Dress like the woman <em className="italic">you're becoming.</em>
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-charcoal-600 leading-relaxed">
              Ava learns your personality, lifestyle, and wardrobe to build outfits that feel
              unmistakably <em>you</em>. Confidence-first styling — not trend-chasing.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" data-testid="hero-cta-primary" className="btn-primary-pill">
                Build my style profile
                <ArrowRight className="ml-2" size={16} />
              </Link>
              <Link to="/login" data-testid="hero-cta-secondary" className="btn-outline-pill">
                I already have an account
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-6 text-xs tracking-[0.2em] uppercase text-charcoal-500">
              <span>✳ No body-shaming, ever</span>
              <span className="hidden sm:inline">✳ Zero trend pressure</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden border border-cream-200 shadow-sm">
              <img src={HERO_IMG} alt="Confident stylish woman" className="w-full h-[540px] object-cover" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-cream-50/90 backdrop-blur-md p-5 border border-cream-200">
                <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500 mb-2">Today's vibe</p>
                <p className="font-serif text-xl italic text-charcoal-800">"Sharp tailoring. Soft heart. You're unstoppable today."</p>
              </div>
            </div>
            <div className="absolute -left-6 -bottom-6 hidden lg:block">
              <div className="bg-accent-lavender px-5 py-3 rounded-full border border-cream-200 text-xs tracking-widest uppercase shadow-sm">
                AI-powered · Private by design
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature bento */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        <p className="text-[0.7rem] tracking-[0.32em] uppercase text-charcoal-500 mb-4">What she does</p>
        <h2 className="font-serif text-3xl sm:text-5xl tracking-tight text-charcoal-900 max-w-2xl">
          A stylist, a best friend, a confidence ritual.
        </h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-6 gap-6">
          <div className="md:col-span-4 md:row-span-2 rounded-3xl overflow-hidden border border-cream-200 bg-cream-100 p-8 flex flex-col justify-between min-h-[420px]">
            <div>
              <CoatHanger size={32} weight="thin" className="text-charcoal-700" />
              <h3 className="font-serif text-3xl mt-6">Your digital wardrobe</h3>
              <p className="mt-3 text-charcoal-600 max-w-md">
                Snap your closet. Ava auto-tags type, color, style and occasion — and starts mixing fresh combinations you've never seen.
              </p>
            </div>
            <img src={GALLERY_IMG} alt="Wardrobe" className="mt-8 w-full h-48 object-cover rounded-2xl" />
          </div>
          <div className="md:col-span-2 rounded-3xl border border-cream-200 bg-accent-sage p-8">
            <ChatCircleDots size={28} weight="thin" />
            <h3 className="font-serif text-2xl mt-6">Chat with Ava</h3>
            <p className="mt-3 text-sm text-charcoal-600">Ask "what should I wear to a Tuesday date?" — get a full look in seconds.</p>
          </div>
          <div className="md:col-span-2 rounded-3xl border border-cream-200 bg-accent-butter p-8">
            <Sparkle size={28} weight="thin" />
            <h3 className="font-serif text-2xl mt-6">Occasion mode</h3>
            <p className="mt-3 text-sm text-charcoal-600">Wedding, interview, first date — outfits tuned to the moment and your mood.</p>
          </div>
          <div className="md:col-span-3 rounded-3xl overflow-hidden border border-cream-200 relative min-h-[260px]">
            <img src={OCCASION_IMG} alt="Occasion" className="absolute inset-0 w-full h-full object-cover" />
            <div className="relative z-10 bg-gradient-to-t from-cream-50 via-cream-50/30 to-transparent h-full p-8 flex flex-col justify-end">
              <h3 className="font-serif text-2xl">Closet optimization</h3>
              <p className="text-sm text-charcoal-600 mt-2 max-w-xs">Rediscover 10 new outfits from clothes you already own.</p>
            </div>
          </div>
          <div className="md:col-span-3 rounded-3xl border border-cream-200 bg-accent-lavender p-8 flex items-center gap-6">
            <Heart size={40} weight="thin" />
            <div>
              <h3 className="font-serif text-2xl">Confidence boost</h3>
              <p className="text-sm text-charcoal-600 mt-2">A daily affirmation written for who you actually are — never generic.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-cream-200 py-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkle weight="fill" size={16} />
            <span className="font-serif text-lg">Ava</span>
          </div>
          <p className="text-xs text-charcoal-500 tracking-widest uppercase">© 2026 Ava Stylist · Made with care</p>
        </div>
      </footer>
    </div>
  );
}
